/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   Rent History bizos
 */

import { Bizo, autoclass } from './base'
import type {
  RawRentHistoryChange,
  RawRentHistoryDetail,
  RawRentHistorySummary,
  RentHistoryChange as RentHistoryChangeType,
  RentHistoryDetail as RentHistoryDetailType,
  RentHistorySummary as RentHistorySummaryType,
} from './rentHistory.types'

export const RentHistoryChange: typeof RentHistoryChangeType = autoclass<
  RentHistoryChangeType,
  RawRentHistoryChange
>(
  'RentHistoryChange',
  class RentHistoryChange extends Bizo<RentHistoryChangeType, RawRentHistoryChange> {},
  {
    bathrooms: 'bathrooms',
    bedrooms: 'bedrooms',
    getRentedAgo: (t: RentHistoryChange) => (now: number) => {
      const today = new Date(now)
      const then = new Date(t.rentedOn * 1000)
      const differenceInMonths =
        today.getMonth() - then.getMonth() + 12 * (today.getFullYear() - then.getFullYear())
      switch (differenceInMonths) {
        case 0:
          return 'This month'
        case 1:
          return 'Last month'
        case 2:
        case 3:
        case 4:
        case 5:
        case 6:
        case 7:
        case 8:
        case 9:
        case 10:
        case 11:
          return `${differenceInMonths} months ago` as const
        default: {
          const differenceInYears = Math.floor(differenceInMonths / 12)
          return differenceInYears === 1
            ? ('1 year ago' as const)
            : (`${differenceInYears} years ago` as const)
        }
      }
    },
    halfBathrooms: 'half_bathrooms',
    listingId: 'listing_id',
    paUrl: 'pa_url',
    priceDelta: 'price_delta',
    rentedOn: 'rented_on',
    rentedPrice: 'rented_price',
    title: 'title',
  }
)

export type RentHistoryChange = RentHistoryChangeType

export const RentHistoryDetail: typeof RentHistoryDetailType = autoclass<
  RentHistoryDetailType,
  RawRentHistoryDetail
>(
  'RentHistoryDetail',
  class RentHistoryDetail extends Bizo<RentHistoryDetailType, RawRentHistoryDetail> {},
  {
    rentHistory: {
      isArray: true,
      name: 'rent_history',
      type: RentHistoryChange,
    },
    totalItems: 'total_items',
  }
)

export type RentHistoryDetail = RentHistoryDetailType

export const RentHistorySummary: typeof RentHistorySummaryType = autoclass<
  RentHistorySummaryType,
  RawRentHistorySummary
>(
  'RentHistorySummary',
  class RentHistorySummary extends Bizo<RentHistorySummaryType, RawRentHistorySummary> {},
  {
    '0': '0',
    '1': '1',
    '2': '2',
    '3+': '3+',
  }
)

export type RentHistorySummary = RentHistorySummaryType
